import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';

import { useShortSelector } from 'react-redux-app/lib/modules/core/hooks';
import { useCurrentLang } from 'react-redux-app/lib/modules/i18n/hooks';
import { useCallCancellablePromiseAndHandleErrorOrExecute } from '../../../core/api/hooks';

import { l } from 'react-redux-app/lib/modules/i18n/utils';
import { getBalanceTotal } from '../../../webmaster/utils/balance';
import { getEditProfileUrl, getBalanceSummaryUrl, getDebtUrl } from '../../utils/siteUrls';

import { getIsActive } from '../../../auth/selectors';
import { getLoadSocialError } from '../../../socialNetworksSettings/selectors/errors';
import {
  getWebmasterBalanceInCredits,
  getWebmasterProfileNick,
} from '../../../webmaster/selectors';

import { actionLogoutRequest } from '../../../auth/actions';
import { actionShowChangePasswordDialog } from '../../../auth/actions/dialogs/changePasswordDialog';
import { actionShowDeleteAccountDialog } from '../../../auth/actions/dialogs/deleteAccountDialog';
import { actionLoadSocialNetworksRequest } from '../../../socialNetworksSettings/actions/loadSocialNetworks';
import { actionShowSocialSettingsDialog } from '../../../socialNetworksSettings/actions/dialogs/socialSettingsDialog';

import moneyFormatterHOC from '../../../core/money/containers/moneyFormatterHOC';

import {
  Menu,
  MenuItem,
  MenuItemLink,
  Submenu,
  SubmenuItem,
  SubmenuItemLink,
  submenu,
} from 'react-redux-app/lib/modules/menu/components';
import BalancePart from '../Balance/BalancePart';

import styles from './styles/index.css.json';


const propTypes = {
  formatMoney: PropTypes.func.isRequired,
};

const WebmasterMenu = ({ formatMoney }) => {
  const dispatch = useDispatch();

  const isUserActive = useShortSelector(getIsActive);
  const balance = useShortSelector(getWebmasterBalanceInCredits);
  const nick = useShortSelector(getWebmasterProfileNick);
  const lang = useCurrentLang();

  const callActionLoadSocialNetworksRequest = (
    useCallCancellablePromiseAndHandleErrorOrExecute(getLoadSocialError)
  );
  const loadSocialNetworkSettings = () => {
    callActionLoadSocialNetworksRequest(
      dispatch(actionLoadSocialNetworksRequest()),
      '',
      {},
      {},
      () => dispatch(actionShowSocialSettingsDialog())
    );
  };

  const logoutRequest = () => { dispatch(actionLogoutRequest()); };

  const submenuItemTheme = { submenuItem: styles.webmasterMenu__submenuItem };

  const getSubmenuItemLink = (to, onClick, textLankLink) => (
    <SubmenuItem key={textLankLink} theme={submenuItemTheme}>
      <SubmenuItemLink
        to={to}
        onClick={onClick}
        theme={{
          submenuItemLink: styles.webmasterMenu__submenuItemLink,
          submenuItemLink_active: styles.webmasterMenu__submenuItemLink_active,
        }}
      >
        {l(textLankLink)}
      </SubmenuItemLink>
    </SubmenuItem>
  );

  const getBalancePart = (nameLangKey, value, link = null, isNegative = false) => (
    <SubmenuItem
      key={nameLangKey}
      theme={submenuItemTheme}
      className={styles.webmasterMenu__submenuItem_balancePart}
    >
      <BalancePart
        name={l(nameLangKey)}
        value={formatMoney(value)}
        link={link}
        isNegative={isNegative}
      />
    </SubmenuItem>
  );

  const activeUserSubmenuItems = [
    getSubmenuItemLink(getEditProfileUrl(lang), null, 'webmaster-menu.update-profile'),
    getSubmenuItemLink(null, loadSocialNetworkSettings, 'webmaster-menu.social-settings'),
    getSubmenuItemLink(
      null,
      () => { dispatch(actionShowChangePasswordDialog()); },
      'webmaster-menu.change-password'
    ),
    getSubmenuItemLink(
      null,
      () => { dispatch(actionShowDeleteAccountDialog()); },
      'webmaster-menu.remove-account'
    ),
  ];

  const balanceTotal = getBalanceTotal(balance);

  return (
    <div className={styles.webmasterMenu}>
      <div className={styles.webmasterMenu__account}>
        {l('msg.account')}
      </div>
      <Menu
        menuItemElementType={MenuItem}
        theme={{ menu: styles.webmasterMenu__menu }}
      >
        <MenuItem
          menuItemLinkElementType={MenuItemLink}
          submenuElementType={Submenu}
          theme={{ menuItem: styles.webmasterMenu__menuItem }}
        >
          <MenuItemLink
            theme={{
              menuItemLink: styles.menuItemLink,
              actionLink: styles.actionLink,
              link: styles.link,
              link_open: styles.link_open,
            }}
            iconAfter={styles.icon_arrow}
          >
            <div>
              <div
                className={classnames(
                  styles.webmasterMenu__balanceTotal,
                  { [styles.webmasterMenu__balanceTotal_negative]: balanceTotal.amount < 0 }
                )}
              >
                {formatMoney(balanceTotal)}
              </div>
              {nick}
            </div>
          </MenuItemLink>
          <Submenu
            align={submenu.SUBMENU_ALIGN_RIGHT}
            theme={{ submenu: styles.webmasterMenu__submenu }}
          >
            {isUserActive && activeUserSubmenuItems}
            {getSubmenuItemLink(null, logoutRequest, 'webmaster-menu.logout')}
            {balance.lossCompensationDebt.amount > 0 && getBalancePart(
              'webmaster-balance.debt',
              balance.lossCompensationDebt,
              isUserActive && getDebtUrl(lang),
              true
            )}
            {getBalancePart(
              'webmaster-balance.revenue', balance.revenue, getBalanceSummaryUrl(lang)
            )}
            {balance.bonus.amount > 0 && getBalancePart(
              'webmaster-balance.sales-bonus', balance.bonus
            )}
          </Submenu>
        </MenuItem>
      </Menu>
    </div>
  );
};

WebmasterMenu.propTypes = propTypes;

export default moneyFormatterHOC(WebmasterMenu);
